import { useState } from 'react';
import { Box, Button, Collapse, Stack, Typography } from '@mui/material';
import type { Booking } from '../../types';
import type { BookingLine } from '../../pricing/pricingEngine';

type Props = {
  booking: Booking;
};

type BookingWithPricingSnapshot = Booking & {
  bookingLines?: BookingLine[] | null;
};

function formatEuro(amount: number) {
  return `${Math.round((amount + Number.EPSILON) * 100) / 100} EUR`;
}

function getBookingLinesSummary(booking: BookingWithPricingSnapshot) {
  if (!Array.isArray(booking.bookingLines) || booking.bookingLines.length === 0) {
    return null;
  }

  const normalizedLines = booking.bookingLines
    .map((line) => {
      const rateId = String(line?.rateId || '').trim();
      const labelSnapshot = String(line?.labelSnapshot || '').trim() || 'Tarif';
      const quantityInitial = Math.max(0, Math.trunc(Number(line?.quantityInitial) || 0));
      const quantityActive = Math.max(0, Math.trunc(Number(line?.quantityActive) || 0));
      const quantityCancelled = Math.max(0, Math.trunc(Number(line?.quantityCancelled) || 0));
      const unitPriceSnapshot = Number(line?.unitPriceSnapshot);
      const lineAmountCurrent = Number(line?.lineAmountCurrent);

      if (
        !rateId
        || !Number.isFinite(unitPriceSnapshot)
        || unitPriceSnapshot < 0
        || !Number.isFinite(lineAmountCurrent)
        || lineAmountCurrent < 0
      ) {
        return null;
      }

      return {
        rateId,
        labelSnapshot,
        quantityInitial,
        quantityActive,
        quantityCancelled,
        unitPriceSnapshot,
        lineAmountCurrent,
      };
    })
    .filter((line): line is NonNullable<typeof line> => Boolean(line));

  if (normalizedLines.length === 0) {
    return null;
  }

  return {
    lines: normalizedLines,
    quantityInitial: normalizedLines.reduce((sum, line) => sum + line.quantityInitial, 0),
    quantityActive: normalizedLines.reduce((sum, line) => sum + line.quantityActive, 0),
    quantityCancelled: normalizedLines.reduce((sum, line) => sum + line.quantityCancelled, 0),
  };
}

function KeyValueRow({
  label,
  value,
  emphasized = false,
}: {
  label: string;
  value: string;
  emphasized?: boolean;
}) {
  return (
    <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="baseline">
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.primary',
          fontWeight: emphasized ? 700 : 600,
          textAlign: 'right',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default function ReservationParticipantsBlock({ booking }: Props) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const bookingSnapshot = booking as BookingWithPricingSnapshot;
  const linesSummary = getBookingLinesSummary(bookingSnapshot);

  const totalParticipants = linesSummary?.quantityInitial ?? Math.max(0, Math.trunc(Number(booking.participantsCount) || 0));
  const cancelledParticipants = linesSummary?.quantityCancelled ?? Math.max(0, Math.trunc(Number(booking.cancelledParticipantsCount) || 0));
  const activeParticipants = linesSummary?.quantityActive
    ?? Math.max(
      0,
      Math.trunc(
        Number.isFinite(Number(booking.activeParticipantsCount))
          ? Number(booking.activeParticipantsCount)
          : totalParticipants - cancelledParticipants,
      ),
    );

  return (
    <Stack spacing={1.25} sx={{ minWidth: 0 }}>
      <Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, letterSpacing: '0.04em' }}>
          Participants
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.25, color: 'text.secondary' }}>
          Vue compacte des quantités et du calcul tarifaire.
        </Typography>
      </Box>

      <Stack spacing={0.75}>
        <KeyValueRow label="Actifs" value={String(activeParticipants)} emphasized />
        <KeyValueRow label="Annulés" value={String(cancelledParticipants)} />
        <KeyValueRow label="Total" value={String(totalParticipants)} />
      </Stack>

      {linesSummary ? (
        <>
          <Button
            variant="text"
            size="small"
            onClick={() => setDetailsOpen((open) => !open)}
            sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0 }}
          >
            {detailsOpen ? 'Masquer détail tarifaire' : 'Voir détail tarifaire'}
          </Button>

          <Collapse in={detailsOpen} timeout="auto" unmountOnExit>
            <Stack spacing={1} sx={{ pt: 0.25 }}>
              {linesSummary.lines.map((line) => (
                <Box
                  key={line.rateId}
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: 'rgba(36, 52, 40, 0.04)',
                    border: '1px solid rgba(36, 52, 40, 0.08)',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {line.labelSnapshot}
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                    <KeyValueRow label="Initial" value={String(line.quantityInitial)} />
                    <KeyValueRow label="Actifs" value={String(line.quantityActive)} />
                    <KeyValueRow label="Annulés" value={String(line.quantityCancelled)} />
                    <KeyValueRow
                      label="Tarif"
                      value={line.unitPriceSnapshot > 0 ? `${formatEuro(line.unitPriceSnapshot)} / pers.` : 'Gratuit'}
                    />
                    <KeyValueRow
                      label="Montant"
                      value={line.lineAmountCurrent > 0 ? formatEuro(line.lineAmountCurrent) : 'Gratuit'}
                      emphasized
                    />
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Collapse>
        </>
      ) : (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Détail tarifaire indisponible.
        </Typography>
      )}
    </Stack>
  );
}